import { useMemo } from 'react';
import { CalendarException } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { Button, CalendarIcon, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { useGetCalendarScheduleV3Data } from '../../hooks/scheduler-v3/use-get-calendar-schedule-v3-data';
import { useGetCalendarV3QueriesData } from '../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useGetCalendarV2QueriesData } from '../../hooks/use-get-calendar-v2-queries-data';
import { useCalendarStore } from '../../stores/calendar.store';
import { transformSchedulerV3EventInfoToCalendarEvent } from '../../utils';
import { CalendarEventsEnums } from '../../views/Calendar/types';

interface Props {
  eventId: string;
  eventTitle: string;
  providerId?: string;
  providerName?: string;
  onActionCallback: () => void;
}

export const OutOfOfficeEventActionMenu = (props: Props) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  return isScheduleV3FlagEnabled ? (
    <OutOfOfficeEventActionMenuV3 {...props} />
  ) : (
    <OutOfOfficeEventActionMenuV2 {...props} />
  );
};

const OutOfOfficeEventActionMenuV2 = (props: Props) => {
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { providerExceptions, officeExceptions } = useGetCalendarV2QueriesData();
  const { handleMenuClick } = useGetManageOutOfOfficeEventMenuClickHandler();

  const eventInfo = useMemo(() => {
    if (props.providerId) {
      return providerExceptions?.find((providerException) => providerException.id === props.eventId);
    } else {
      return officeExceptions?.find((officeHoursException) => officeHoursException.id === props.eventId);
    }
  }, [props, providerExceptions, officeExceptions]);

  if (!eventInfo) {
    return null;
  }

  return (
    <OutOfOfficeEventActionMenuComponent
      onManageOutOfOfficeEventMenuClick={() => {
        handleMenuClick(eventInfo, props, calendarDate);
        props.onActionCallback();
      }}
    />
  );
};

const OutOfOfficeEventActionMenuV3 = (props: Props) => {
  const { handleMenuClick } = useGetManageOutOfOfficeEventMenuClickHandler();
  const { providersData, calendarLocationIds } = useGetCalendarV3QueriesData();
  const { providerSchedulesQuery } = useGetCalendarScheduleV3Data(
    calendarLocationIds.map((id) => ({
      locationId: id,
      providerIds: providersData?.providers.filter((p) => p.locationId === id).map((p) => p.id),
      operatoryIds: [],
    }))
  );

  const eventInfo = useMemo(() => {
    const eventInfoV3 = (props.providerId ? providerSchedulesQuery.data : [])?.find((entry) =>
      entry.recurrenceRules?.override?.unAvailabilities?.find((unAvailability) => unAvailability.id === props.eventId)
    );

    return eventInfoV3
      ? transformSchedulerV3EventInfoToCalendarEvent(eventInfoV3, props.eventId, props.providerId || '')
      : undefined;
  }, [props.eventId, props.providerId, providerSchedulesQuery.data]);

  if (!eventInfo) {
    return null;
  }

  return (
    <OutOfOfficeEventActionMenuComponent
      onManageOutOfOfficeEventMenuClick={() => {
        handleMenuClick(eventInfo, props, formatDate(eventInfo.startDateTime, 'MM/DD/YYYY'), true);
        props.onActionCallback();
      }}
    />
  );
};

const OutOfOfficeEventActionMenuComponent = ({
  onManageOutOfOfficeEventMenuClick,
}: {
  onManageOutOfOfficeEventMenuClick: () => void;
}) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  const popoverMenuProps = usePopoverMenu({ placement: 'right' });
  return (
    <>
      <Button
        variant='secondary'
        iconName='more-small'
        {...popoverMenuProps.getTriggerProps()}
        aria-label={t('More Icon')}
      />
      <PopoverMenu {...popoverMenuProps.getMenuProps()}>
        <PopoverMenuItem
          Icon={CalendarIcon}
          {...popoverMenuProps.getItemProps({ index: 0, onClick: onManageOutOfOfficeEventMenuClick })}
        >
          {t('Manage Out of Office Event')}
        </PopoverMenuItem>
      </PopoverMenu>
    </>
  );
};

const useGetManageOutOfOfficeEventMenuClickHandler = () => {
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');

  const handleMenuClick = (
    eventInfo: CalendarException,
    { eventId, providerId, providerName, eventTitle }: Props,
    selectedDate: string,
    isV3Event = false
  ) => {
    const startDate = isV3Event ? dayjs.utc(eventInfo.startDateTime) : dayjs(eventInfo.startDateTime);
    const endDate = isV3Event ? dayjs.utc(eventInfo.endDateTime) : dayjs(eventInfo.endDateTime);

    setConfiguredCalendarEvent({
      eventId,
      isValid: true,
      calendarDateValue: selectedDate,
      locationId: eventInfo.locationId ?? '',
      calendarEventType: providerId
        ? CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT
        : CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT,
      startDate: formatDate(startDate, 'MM/DD/YYYY'),
      endDate: formatDate(endDate, 'MM/DD/YYYY'),
      startHour: formatDate(startDate, 'hh:mm A'),
      endHour: formatDate(endDate, 'hh:mm A'),
      providerId: providerId,
      providerName: providerName,
      name: eventTitle,
    });
  };

  return { handleMenuClick };
};
